import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { recibosApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSucursal } from '../context/SucursalContext';
import { formatDateTime } from '../utils/dateHelpers';

const ReciboDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sucursalActual } = useSucursal();

  const [recibo, setRecibo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRecibo = async () => {
      try {
        const data = await recibosApi.getById(id);
        setRecibo(data);
      } catch (err) {
        console.error('Error:', err);
        alert('Recibo no encontrado');
        navigate('/recibos');
      } finally {
        setLoading(false);
      }
    };
    loadRecibo();
  }, [id, navigate]);

  const formatMoney = (amount) => `Q${parseFloat(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${date.getDate()} de ${meses[date.getMonth()]} de ${date.getFullYear()}`;
  };

  const handlePrint = () => {
    if (!recibo) return;

    const printWindow = window.open('about:blank', '_blank', 'width=400,height=600');
    if (!printWindow) {
      alert('Por favor habilite las ventanas emergentes para imprimir');
      return;
    }

    const tiendaNombre = sucursalActual?.nombre || recibo.nombre_tienda || 'INTERSECOM';
    const tiendaDireccion = sucursalActual?.direccion || '15 Av. 1-340 Zona 5, San Marcos';
    const tiendaTelefono = sucursalActual?.telefono || '7760-3991 / 7725-4830';
    const items = recibo.items || [];

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Recibo de Caja N° ${recibo.numero_recibo}</title>
          <style>
            @media print {
              @page { size: 72mm auto; margin: 0 !important; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              body {
                font-family: Arial, Helvetica, sans-serif !important;
                font-size: 11px !important; font-weight: 600 !important; line-height: 1.3 !important;
                margin: 0 !important; padding: 0 !important; width: 72mm !important;
                color: #000 !important; background: #fff !important;
                text-shadow: 0.3px 0 0 #000, -0.3px 0 0 #000 !important;
              }
              .rc { width: 72mm !important; padding: 2mm !important; box-sizing: border-box !important; }
              .rc-header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 3mm; margin-bottom: 3mm; }
              .rc-logo { font-size: 16px !important; font-weight: 900 !important; letter-spacing: 1px; }
              .rc-sub { font-size: 10px !important; font-weight: 700 !important; margin-bottom: 2px; }
              .rc-tipo { text-align: center; font-size: 13px !important; font-weight: 900 !important; margin: 3mm 0; padding: 2mm; border: 2px solid #000; }
              .rc-num { text-align: center; font-size: 12px !important; font-weight: 800 !important; margin-bottom: 3mm; }
              .rc-row { display: flex; justify-content: space-between; font-size: 10px !important; font-weight: 600 !important; margin-bottom: 1mm; }
              .rc-label { font-weight: 800 !important; }
              .rc-table { width: 100%; border-collapse: collapse; margin: 3mm 0; }
              .rc-table th { border-bottom: 2px solid #000; padding: 2mm 1mm; font-weight: 900 !important; font-size: 9px !important; }
              .rc-table td { padding: 2mm 1mm; border-bottom: 1px dashed #000; font-size: 10px !important; }
              .rc-table .cc { width: 12%; text-align: center; }
              .rc-table .cd { width: 48%; }
              .rc-table .cp { width: 18%; text-align: right; }
              .rc-table .ct { width: 22%; text-align: right; font-weight: 700 !important; }
              .rc-total-s { border-top: 2px solid #000; padding-top: 3mm; margin-top: 2mm; }
              .rc-letras { font-size: 10px !important; font-weight: 700 !important; margin-bottom: 2mm; font-style: italic; }
              .rc-total { display: flex; justify-content: space-between; font-size: 14px !important; font-weight: 900 !important; padding: 2mm; border: 2px solid #000; margin-top: 2mm; }
              .rc-firmas { display: flex; justify-content: space-between; margin-top: 10mm; }
              .rc-firma { text-align: center; width: 45%; }
              .rc-firma-l { border-top: 1px solid #000; margin-bottom: 2mm; margin-top: 8mm; }
              .rc-firma-t { font-size: 9px !important; font-weight: 700 !important; }
              .rc-footer { text-align: center; font-size: 9px !important; margin-top: 4mm; padding-top: 3mm; border-top: 1px solid #000; }
              .rc-corte { text-align: center; margin-top: 5mm; padding-top: 3mm; border-top: 2px dashed #000; font-size: 10px !important; }
            }
          </style>
        </head>
        <body>
          <div class="rc">
            <div class="rc-header">
              <div class="rc-logo">${tiendaNombre}</div>
              <div class="rc-sub">DE TODO EN COMPUTACION</div>
              <div class="rc-sub">${tiendaDireccion}</div>
              <div class="rc-sub">Tel: ${tiendaTelefono}</div>
            </div>
            <div class="rc-tipo">RECIBO DE CAJA</div>
            <div class="rc-num">N° ${recibo.numero_recibo}</div>
            <div style="margin-bottom:3mm;">
              <div class="rc-row"><span class="rc-label">Fecha:</span><span>${formatDateLong(recibo.fecha)}</span></div>
              <div class="rc-row"><span class="rc-label">Recibi de:</span><span>${recibo.recibido_de}</span></div>
              ${recibo.direccion ? `<div class="rc-row"><span class="rc-label">Direccion:</span><span>${recibo.direccion}</span></div>` : ''}
              <div class="rc-row"><span class="rc-label">NIT:</span><span>${recibo.nit || 'C/F'}</span></div>
            </div>
            <table class="rc-table">
              <thead><tr><th class="cc">CANT</th><th class="cd">DESCRIPCION</th><th class="cp">P/UNIT</th><th class="ct">TOTAL</th></tr></thead>
              <tbody>
                ${items.map(item => `<tr><td class="cc">${parseFloat(item.cantidad).toFixed(0)}</td><td class="cd">${item.descripcion}</td><td class="cp">${formatMoney(item.precio_unitario)}</td><td class="ct">${formatMoney(item.total)}</td></tr>`).join('')}
              </tbody>
            </table>
            <div class="rc-total-s">
              <div class="rc-letras">EN LETRAS: ${recibo.total_letras || ''}</div>
              <div class="rc-total"><span>TOTAL Q.</span><span>${formatMoney(recibo.total).replace('Q', '')}</span></div>
            </div>
            <div class="rc-firmas">
              <div class="rc-firma"><div class="rc-firma-l"></div><div class="rc-firma-t">${recibo.nombre_creador || user?.nombre || ''}</div></div>
              <div class="rc-firma"><div class="rc-firma-l"></div><div class="rc-firma-t">FIRMA</div></div>
            </div>
            <div class="rc-footer"><p>Gracias por su preferencia</p></div>
            <div class="rc-corte">- - - - - - - - - - - - - - - - - - -</div>
          </div>
          <script>window.addEventListener('load', function() { setTimeout(function() { window.print(); }, 500); });</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handleEliminar = async () => {
    if (!window.confirm(`¿Eliminar el recibo N° ${recibo.numero_recibo}?`)) return;
    try {
      await recibosApi.delete(recibo.id);
      navigate('/recibos');
    } catch (err) {
      alert('Error al eliminar: ' + (err.response?.data?.error || err.message));
    }
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  if (!recibo) {
    return <div className="empty-state"><h3>Recibo no encontrado</h3></div>;
  }

  let desglosePagoMixto = [];
  if (recibo.metodo_pago === 'Mixto' && recibo.detalle_pago_mixto) {
    try {
      desglosePagoMixto = JSON.parse(recibo.detalle_pago_mixto);
    } catch (e) {
      desglosePagoMixto = [];
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Recibo N° {recibo.numero_recibo}</h1>
          <p className="page-subtitle">{formatDateLong(recibo.fecha)} - {recibo.recibido_de}</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={() => navigate('/recibos')} className="btn btn-secondary">
            Volver
          </button>
          <button onClick={handlePrint} className="btn btn-primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Reimprimir
          </button>
          <button onClick={handleEliminar} className="btn btn-danger"
            style={{ background: '#e53e3e', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 16px', cursor: 'pointer' }}>
            Eliminar
          </button>
        </div>
      </div>

      <div className="card">
        {/* Datos del recibo */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>N° Recibo</div>
            <div style={{ fontSize: '18px', fontWeight: '700' }}>{recibo.numero_recibo}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Fecha</div>
            <div style={{ fontWeight: '500' }}>{formatDateLong(recibo.fecha)}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Recibi de</div>
            <div style={{ fontWeight: '600' }}>{recibo.recibido_de}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>NIT</div>
            <div>{recibo.nit || 'C/F'}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Método de Pago</div>
            <div>{recibo.metodo_pago || 'Efectivo'}</div>
            {desglosePagoMixto.length > 0 && (
              <div style={{ marginTop: '6px' }}>
                {desglosePagoMixto.map((p, i) => (
                  <div key={i} style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                    {p.metodo}: <strong>{formatMoney(p.monto)}</strong>
                    {p.metodo === 'Cheque' && p.comprobante ? ` (${p.comprobante})` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
          {recibo.direccion && (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Direccion</div>
              <div>{recibo.direccion}</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Creado por</div>
            <div>{recibo.nombre_creador || '-'}</div>
          </div>
          {recibo.nombre_tienda && (
            <div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '4px' }}>Tienda</div>
              <div>{recibo.nombre_tienda}</div>
            </div>
          )}
        </div>

        {/* Items del recibo */}
        <h3 style={{ fontSize: '14px', color: 'var(--text-light)', textTransform: 'uppercase', marginBottom: '12px' }}>
          Detalle del Recibo
        </h3>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '80px' }}>Cantidad</th>
                <th>Descripcion</th>
                <th style={{ textAlign: 'right', width: '120px' }}>P. Unitario</th>
                <th style={{ textAlign: 'right', width: '120px' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {(recibo.items || []).map((item, i) => (
                <tr key={i}>
                  <td style={{ textAlign: 'center' }}>{parseFloat(item.cantidad).toFixed(0)}</td>
                  <td>{item.descripcion}</td>
                  <td style={{ textAlign: 'right' }}>{formatMoney(item.precio_unitario)}</td>
                  <td style={{ textAlign: 'right', fontWeight: '600' }}>{formatMoney(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Totales */}
        <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', marginTop: '16px' }}>
          {recibo.total_letras && (
            <div style={{ marginBottom: '12px', fontStyle: 'italic', color: 'var(--text-light)' }}>
              EN LETRAS: {recibo.total_letras}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '16px' }}>
            <span style={{ fontSize: '18px', fontWeight: '600' }}>TOTAL:</span>
            <span style={{ fontSize: '28px', fontWeight: '700', color: 'var(--primary-color)' }}>
              {formatMoney(recibo.total)}
            </span>
          </div>
        </div>

        {/* Metadata */}
        <div style={{ marginTop: '16px', fontSize: '12px', color: 'var(--text-light)', textAlign: 'right' }}>
          Creado: {formatDateTime(recibo.created_at)}
        </div>
      </div>
    </div>
  );
};

export default ReciboDetalle;
